'use client';

import { useState } from 'react';
import { interpolateStringWithData } from 'utils';
import { useBreakpoints } from '../../../lib/hooks/useBreakpoints';
import {
  CollapsiblePanelContentLite,
  CollapsiblePanelItemLite,
  CollapsiblePanelLite,
  CollapsiblePanelTriggerLite,
} from '../CollapsiblePanelLite';
import { ExpandableGroupProps } from './ExpandableGroup.types';

function VersionList({
  versions,
  previewCount,
  isMobile,
}: {
  versions: string[];
  previewCount: number;
  isMobile?: boolean;
}) {
  const rows: string[][] = [];
  for (let i = 0; i < versions.length; i += previewCount) {
    rows.push(versions.slice(i, i + previewCount));
  }

  return isMobile ? (
    <div className="max-w-[160px] text-right text-sm font-medium text-content-muted">
      {versions.map((version, idx) => (
        <span key={version}>
          {version}
          {idx < versions.length - 1 && ', '}
        </span>
      ))}
    </div>
  ) : (
    <div className="flex flex-col items-end">
      {rows.map((row, rowIdx) => (
        <div key={row.join('-')} className="flex">
          {row.map((version, idx) => {
            const isLast = rowIdx === rows.length - 1 && idx === row.length - 1;
            return (
              <span
                key={version}
                className="inline-block text-sm font-medium text-content-muted"
              >
                {version}
                {!isLast ? (
                  <span className="text-content-muted">,&nbsp;</span>
                ) : (
                  <span className="text-grey-100">&nbsp;</span>
                )}
              </span>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export function ExpandableGroup({
  microcopy,
  unityVersionList,
  className = '',
}: ExpandableGroupProps) {
  const [isOpen, setIsOpen] = useState(false);
  const breakpoints = useBreakpoints();
  const PREVIEW_COUNT = breakpoints?.isMobile ? 9 : 3;

  const needsTruncation =
    unityVersionList && unityVersionList.length > PREVIEW_COUNT;
  const previewVersions = needsTruncation
    ? unityVersionList.slice(0, PREVIEW_COUNT)
    : (unityVersionList ?? []);

  const collapsedMicrocopy = interpolateStringWithData(
    microcopy.collapsedTitle,
    {
      unityVersion:
        unityVersionList && unityVersionList.length > 0
          ? unityVersionList.length.toString()
          : '',
    }
  );

  return (
    <div
      className={`max-w-[400px] rounded-lg bg-surface-muted p-4 ${className}`.trim()}
    >
      <div className="flex justify-between text-sm text-content-subtle">
        <span
          className={`mr-2 ${breakpoints?.isMobile ? '' : 'shrink-0 whitespace-normal'}`}
        >
          {microcopy.supportedUnityVersions}
        </span>

        {isOpen ? (
          <VersionList
            versions={unityVersionList ?? []}
            previewCount={PREVIEW_COUNT}
            isMobile={breakpoints?.isMobile}
          />
        ) : (
          <VersionList
            versions={previewVersions}
            previewCount={PREVIEW_COUNT}
            isMobile={breakpoints?.isMobile}
          />
        )}
      </div>

      {needsTruncation && (
        <CollapsiblePanelLite
          type="single"
          collapsible
          onValueChange={(value) => setIsOpen(!!value)}
        >
          <CollapsiblePanelItemLite value="versions">
            <CollapsiblePanelContentLite />
            <CollapsiblePanelTriggerLite>
              {isOpen ? (
                <span className="text-link">{microcopy.expandedTitle}</span>
              ) : (
                <span className="group-hover:text-link">
                  {collapsedMicrocopy}
                </span>
              )}
            </CollapsiblePanelTriggerLite>
          </CollapsiblePanelItemLite>
        </CollapsiblePanelLite>
      )}
    </div>
  );
}
